import { motion } from "framer-motion";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { useTutorialStore } from "../../stores/tutorialStore";
import type { TutorialStepDef } from "./TutorialStepContent";
import { PHASE_LABELS, PHASE_ORDER, TUTORIAL_STEPS } from "./TutorialStepContent";

interface TutorialStepProps {
	step: TutorialStepDef;
}

export function TutorialStep({ step }: TutorialStepProps) {
	const { isTouchDevice } = useDeviceCapabilities();
	const nextStep = useTutorialStore((s) => s.nextStep);
	const prevStep = useTutorialStore((s) => s.prevStep);
	const dismiss = useTutorialStore((s) => s.dismissTutorial);

	const description =
		isTouchDevice && step.descriptionMobile ? step.descriptionMobile : step.description;

	const phaseIndex = PHASE_ORDER.indexOf(step.phase);
	const isOutcomeStep = step.id === 16;

	return (
		<motion.div
			className={`tutorial-card tutorial-card--${step.position} glass-panel-elevated`}
			initial={{ opacity: 0, y: 16, scale: 0.96 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			exit={{ opacity: 0, y: -10, scale: 0.98 }}
			transition={{ type: "spring", damping: 25, stiffness: 300 }}
			key={step.id}
		>
			{/* Phase progress dots */}
			<div className="tutorial-card__progress">
				{PHASE_ORDER.map((phase, i) => (
					<div
						key={phase}
						className={`tutorial-card__progress-dot${
							i < phaseIndex
								? " tutorial-card__progress-dot--completed"
								: i === phaseIndex
									? " tutorial-card__progress-dot--active"
									: ""
						}`}
					/>
				))}
			</div>

			<span className="tutorial-card__phase">
				{PHASE_LABELS[step.phase]} &middot; {step.id + 1}/{TUTORIAL_STEPS.length}
			</span>

			<h3 className="tutorial-card__title">{step.title}</h3>
			<p className="tutorial-card__description">{description}</p>

			{isOutcomeStep && (
				<div className="tutorial-card__outcomes">
					<span className="tutorial-card__outcome tutorial-card__outcome--poor">Poor</span>
					<span className="tutorial-card__outcome tutorial-card__outcome--standard">Standard</span>
					<span className="tutorial-card__outcome tutorial-card__outcome--rich">Rich</span>
					<span className="tutorial-card__outcome tutorial-card__outcome--jackpot">Jackpot</span>
					<span className="tutorial-card__outcome tutorial-card__outcome--destroyed">
						Destroyed
					</span>
				</div>
			)}

			<div className="tutorial-card__actions">
				{step.showBackButton && (
					<button type="button" className="tutorial-card__btn-back" onClick={prevStep}>
						Back
					</button>
				)}
				<button type="button" className="btn btn-primary tutorial-card__btn-primary" onClick={nextStep}>
					{step.buttonLabel}
				</button>
			</div>

			{/* Skip tutorial link - not shown on welcome/completion cards */}
			{step.id !== 0 && step.id !== TUTORIAL_STEPS.length - 1 && (
				<button type="button" className="tutorial-card__skip" onClick={dismiss}>
					Skip tutorial
				</button>
			)}
		</motion.div>
	);
}
